import { useCallback, useEffect, useState } from 'react';
import { bookingApi, BookingApiError } from '../../lib/api';
import { errorKeyFor, useT } from '../../lib/i18n';
import { ErrorNote, ScreenHeading, Skeleton } from '../shell/PortalShell';
import { BookingCard } from './BookingCard';
import { RescheduleSheet } from './RescheduleSheet';
import type { Booking, Site } from '../../lib/types';

/**
 * "My bookings" — upcoming and past, with self-service cancellation.
 *
 * Whether cancel or reschedule is offered comes from the server (`can_cancel`,
 * `can_reschedule`), which applies the tenant's cutoff windows. The client only
 * renders that decision; it never tries to work the rules out itself, so a
 * tenant changing its policy takes effect without a deploy.
 */
export function MyBookings({ slug, site }: { slug: string; site: Site }): JSX.Element {
  const { t, lang } = useT();
  const [scope, setScope] = useState<'upcoming' | 'past'>('upcoming');
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [rescheduling, setRescheduling] = useState<Booking | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setBookings(await bookingApi.bookings(slug, scope));
    } catch (err) {
      setError(err instanceof BookingApiError ? t(errorKeyFor(err.code)) : t('err_generic'));
    } finally {
      setLoading(false);
    }
  }, [slug, scope, t]);

  useEffect(() => {
    void load();
  }, [load]);

  async function cancel(booking: Booking): Promise<void> {
    if (!window.confirm(t('cancel_confirm'))) return;
    setBusyId(booking.session_id);
    setError(null);
    try {
      const updated = await bookingApi.cancel(slug, booking.session_id);
      setBookings((prev) => prev.map((b) => (b.session_id === updated.session_id ? updated : b)));
    } catch (err) {
      setError(err instanceof BookingApiError ? err.message : t('err_generic'));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="bk-rise">
      <ScreenHeading title={t('my_bookings')} />

      <div className="mb-4 inline-flex rounded-xl bg-zinc-100 p-1" role="tablist">
        {(['upcoming', 'past'] as const).map((key) => (
          <button
            key={key}
            role="tab"
            aria-selected={scope === key}
            onClick={() => setScope(key)}
            className={`rounded-lg px-4 py-2 text-sm font-medium transition ${
              scope === key ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500'
            }`}
          >
            {t(key)}
          </button>
        ))}
      </div>

      {error && <ErrorNote message={error} onRetry={() => void load()} />}

      {loading ? (
        <Skeleton rows={3} />
      ) : bookings.length === 0 ? (
        <p className="bk-card p-8 text-center text-sm text-zinc-500">{t('no_bookings')}</p>
      ) : (
        <div className="space-y-3">
          {bookings.map((booking) => (
            <BookingCard
              key={booking.session_id}
              booking={booking}
              actions={
                booking.is_cancelled ? (
                  <span className="bk-chip bg-zinc-100 text-zinc-500">{t('cancelled')}</span>
                ) : booking.can_cancel || booking.can_reschedule ? (
                  <>
                    {booking.can_reschedule && (
                      <button
                        className="bk-btn bk-btn-ghost px-4 py-2 text-xs"
                        onClick={() => setRescheduling(booking)}
                      >
                        {t('reschedule')}
                      </button>
                    )}
                    {booking.can_cancel && (
                      <button
                        className="bk-btn bk-btn-quiet px-4 py-2 text-xs text-rose-600 hover:text-rose-700"
                        disabled={busyId === booking.session_id}
                        onClick={() => void cancel(booking)}
                      >
                        {busyId === booking.session_id ? t('loading') : t('cancel_booking')}
                      </button>
                    )}
                  </>
                ) : (
                  <span className="text-xs text-zinc-400">
                    {lang === 'ar'
                      ? 'للتغيير، يرجى الاتصال بنا.'
                      : 'To make changes, please call us.'}
                  </span>
                )
              }
            />
          ))}
        </div>
      )}

      {rescheduling && (
        <RescheduleSheet
          slug={slug}
          site={site}
          booking={rescheduling}
          onClose={() => setRescheduling(null)}
          onDone={(updated) => {
            setBookings((prev) => prev.map((b) => (b.session_id === updated.session_id ? updated : b)));
            setRescheduling(null);
          }}
        />
      )}
    </div>
  );
}
